import { DEFAULT_BOARD_PREFERENCES, type UserSettings } from "@chessla/shared";

const LEGACY_KEYS = ["chessla.review-mode", "chessla:review-board-settings", "chessla-board-display"] as const;

type ReadStorage = Pick<Storage, "getItem">;
type RemoveStorage = Pick<Storage, "removeItem">;

const read = (storage: ReadStorage | null, key: string): string | null => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const readLegacyUserSettings = (storage: ReadStorage | null): Partial<UserSettings> => {
  const settings: Partial<UserSettings> = {};
  const reviewMode = read(storage, LEGACY_KEYS[0]);
  if (reviewMode === "guided" || reviewMode === "open") settings.reviewMode = reviewMode;

  try {
    const board = JSON.parse(read(storage, LEGACY_KEYS[1]) ?? "null") as unknown;
    if (record(board)) {
      if (
        board.evaluationPreset === "quick" ||
        board.evaluationPreset === "balanced" ||
        board.evaluationPreset === "deep"
      ) {
        settings.evaluationPreset = board.evaluationPreset;
      }
      const copyBoolean = (
        key: keyof Pick<
          UserSettings,
          | "useMaiaReplies"
          | "showLegalMoves"
          | "showMotifs"
          | "showPrincipleSignals"
          | "showBestMoveArrow"
          | "showPositionMarkers"
          | "showAllPositionMarkers"
        >,
      ) => {
        if (typeof board[key] === "boolean") Object.assign(settings, { [key]: board[key] });
      };
      copyBoolean("useMaiaReplies");
      copyBoolean("showLegalMoves");
      copyBoolean("showMotifs");
      copyBoolean("showPrincipleSignals");
      copyBoolean("showBestMoveArrow");
      copyBoolean("showPositionMarkers");
      copyBoolean("showAllPositionMarkers");
      if (record(board.markerGroups)) {
        if (typeof board.markerGroups.pin === "boolean") settings.markerPins = board.markerGroups.pin;
        if (typeof board.markerGroups.support === "boolean") settings.markerSupport = board.markerGroups.support;
      }
    }
  } catch {
    // Invalid legacy JSON has no settings to migrate.
  }

  try {
    const board: unknown = JSON.parse(read(storage, LEGACY_KEYS[2]) ?? "null");
    if (record(board)) {
      const mapping = {
        sound: "moveSounds",
        evaluation: "showEvaluation",
        motifs: "showMotifs",
        tactics: "showTacticalMotifs",
        principles: "showPrincipleSignals",
        markers: "showPositionMarkers",
        allMarkers: "showAllPositionMarkers",
        bestArrow: "showBestMoveArrow",
        legalMoves: "showLegalMoves",
      } as const;
      const valid = Object.entries(mapping).filter(([key]) => typeof board[key] === "boolean");
      if (valid.length)
        settings.freeBoardSettings = {
          ...DEFAULT_BOARD_PREFERENCES,
          evaluationPreset: "quick",
          ...Object.fromEntries(valid.map(([key, target]) => [target, board[key]])),
        };
    }
  } catch {
    // Invalid legacy JSON has no settings to migrate.
  }
  return settings;
};

export const clearLegacyUserSettings = (storage: RemoveStorage | null): void => {
  for (const key of LEGACY_KEYS) {
    try {
      storage?.removeItem(key);
    } catch {
      /* Browser storage is optional. */
    }
  }
};
